import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  ShieldAlert,
  Trash2,
  Upload,
} from "lucide-react";
import { hostApplicationService, type SaveHostApplicationPayload } from "@/lib/services";
import { useAuth } from "@/lib/auth-context";
import { extractErrorMessage } from "@/lib/api";
import { PageShell } from "@/components/nav";
import type { AcceptedDocumentType, HostApplication } from "@/types/api";

export const Route = createFileRoute("/_authenticated/become-host")({
  component: BecomeHostPage,
});

const TONE_CLASSES: Record<HostApplication["tone"], string> = {
  success: "border-primary/40 bg-primary/5 text-ink",
  danger: "border-destructive/40 bg-destructive/5 text-ink",
  warning: "border-accent bg-accent/20 text-ink",
  info: "border-border bg-muted/40 text-ink",
  neutral: "border-border bg-muted/30 text-ink",
};

/**
 * Applying to become a host.
 *
 * One screen for the whole lifecycle rather than a wizard. An applicant coming
 * back after being asked for more information needs to see what was asked, what
 * they already sent, and what to change — all at once. A stepper would hide two
 * of those three behind navigation.
 */
function BecomeHostPage() {
  const { user, refresh } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isPending } = useQuery({
    queryKey: ["host-application"],
    queryFn: () => hostApplicationService.get(),
  });

  const application = data?.data ?? null;
  const accepted = data?.accepted_documents ?? [];

  const refreshAll = async () => {
    await queryClient.invalidateQueries({ queryKey: ["host-application"] });
    await refresh();
  };

  // An approved host belongs in the host area, not on the application form.
  useEffect(() => {
    if (application?.state === "approved") {
      navigate({ to: "/host" });
    }
  }, [application?.state, navigate]);

  if (isPending) {
    return (
      <PageShell>
        <div className="mx-auto max-w-2xl space-y-4 px-4 py-10">
          <div className="h-8 w-1/2 animate-pulse rounded-full brand-skeleton" />
          <div className="h-40 animate-pulse rounded-2xl brand-skeleton" />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="mx-auto max-w-2xl space-y-6 px-4 py-10">
        <header>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary">
            Hosting on CavaYo
          </p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-ink">
            Become a host
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-ink-muted">
            We verify every host before they can take bookings. It protects guests, and it means
            your listings carry a badge people trust. You will need one photo ID.
          </p>
        </header>

        {application && (
          <div className={`rounded-2xl border p-4 ${TONE_CLASSES[application.tone]}`}>
            <p className="flex items-center gap-2 font-bold">
              <StateIcon state={application.state} />
              {application.state_label}
            </p>
            <p className="mt-1 text-sm text-ink-muted">{application.state_message}</p>
            {application.decision_message && (
              <p className="mt-2 rounded-xl border border-border bg-card p-3 text-sm text-ink">
                <span className="font-bold">From our team:</span> {application.decision_message}
              </p>
            )}
          </div>
        )}

        <ApplicationForm
          application={application}
          onSaved={refreshAll}
          defaultName={user?.name ?? ""}
          defaultPhone={user?.phone ?? ""}
        />

        {application && (
          <DocumentsSection application={application} accepted={accepted} onChanged={refreshAll} />
        )}

        {application && <SubmitSection application={application} onSubmitted={refreshAll} />}

        {!application && (
          <p className="text-xs text-ink-subtle">
            Already hosting elsewhere?{" "}
            <Link to="/" className="font-bold text-primary hover:underline">
              Keep browsing
            </Link>{" "}
            while you decide.
          </p>
        )}
      </div>
    </PageShell>
  );
}

function StateIcon({ state }: { state: HostApplication["state"] }) {
  if (state === "approved") return <BadgeCheck className="h-4 w-4 text-primary" />;
  if (state === "rejected" || state === "suspended")
    return <ShieldAlert className="h-4 w-4 text-destructive" />;
  if (state === "needs_more_info") return <ShieldAlert className="h-4 w-4" />;
  return <Clock className="h-4 w-4" />;
}

function ApplicationForm({
  application,
  onSaved,
  defaultName,
  defaultPhone,
}: {
  application: HostApplication | null;
  onSaved: () => Promise<void>;
  defaultName: string;
  defaultPhone: string;
}) {
  const [form, setForm] = useState<SaveHostApplicationPayload>({
    legal_name: application?.legal_name ?? defaultName,
    phone: application?.phone ?? defaultPhone,
    residential_address: application?.residential_address ?? "",
    national_id_number: application?.national_id_number ?? "",
    business_name: application?.business_name ?? "",
    about: application?.about ?? "",
  });

  const editable = application?.can_edit ?? true;

  const save = useMutation({
    mutationFn: () => hostApplicationService.save(form),
    onSuccess: async () => {
      toast.success(application ? "Details saved." : "Application started.");
      await onSaved();
    },
    onError: (error) => toast.error(extractErrorMessage(error, "Couldn't save your details")),
  });

  const set = (key: keyof SaveHostApplicationPayload, value: string) =>
    setForm((previous) => ({ ...previous, [key]: value }));

  return (
    <section className="rounded-2xl border border-border bg-card p-5">
      <h2 className="text-base font-extrabold tracking-tight text-ink">Your details</h2>
      <p className="mt-1 text-xs text-ink-muted">
        Use the name exactly as it appears on the ID you will upload.
      </p>

      <form
        className="mt-4 space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <Field label="Full legal name" required>
          <input
            value={form.legal_name}
            onChange={(event) => set("legal_name", event.target.value)}
            disabled={!editable}
            className={inputClass}
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Phone" required>
            <input
              value={form.phone}
              onChange={(event) => set("phone", event.target.value)}
              inputMode="tel"
              placeholder="+256 700 000 000"
              disabled={!editable}
              className={inputClass}
            />
          </Field>
          <Field label="ID number">
            <input
              value={form.national_id_number ?? ""}
              onChange={(event) => set("national_id_number", event.target.value)}
              disabled={!editable}
              className={inputClass}
            />
          </Field>
        </div>

        <Field label="Home address" required>
          <input
            value={form.residential_address}
            onChange={(event) => set("residential_address", event.target.value)}
            disabled={!editable}
            className={inputClass}
          />
        </Field>

        <Field label="Business name">
          <input
            value={form.business_name ?? ""}
            onChange={(event) => set("business_name", event.target.value)}
            placeholder="Only if you host through a registered business"
            disabled={!editable}
            className={inputClass}
          />
        </Field>

        <Field label="About you">
          <textarea
            value={form.about ?? ""}
            onChange={(event) => set("about", event.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="A line or two about the places you plan to host."
            disabled={!editable}
            className={`${inputClass} h-auto py-2`}
          />
        </Field>

        {editable && (
          <button
            type="submit"
            disabled={save.isPending}
            className="inline-flex h-11 items-center gap-2 rounded-xl bg-primary px-5 text-sm font-bold text-primary-foreground transition hover:opacity-90 disabled:opacity-60"
          >
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {application ? "Save details" : "Start application"}
          </button>
        )}
      </form>
    </section>
  );
}

function DocumentsSection({
  application,
  accepted,
  onChanged,
}: {
  application: HostApplication;
  accepted: AcceptedDocumentType[];
  onChanged: () => Promise<void>;
}) {
  const [type, setType] = useState(accepted[0]?.value ?? "national_id");
  const fileRef = useRef<HTMLInputElement>(null);

  const upload = useMutation({
    mutationFn: (file: File) => hostApplicationService.uploadDocument(type, file),
    onSuccess: async () => {
      toast.success("Document uploaded.");
      await onChanged();
    },
    onError: (error) => toast.error(extractErrorMessage(error, "Couldn't upload that file")),
  });

  const remove = useMutation({
    mutationFn: (id: string) => hostApplicationService.removeDocument(id),
    onSuccess: async () => {
      toast.success("Document removed.");
      await onChanged();
    },
    onError: (error) => toast.error(extractErrorMessage(error, "Couldn't remove that document")),
  });

  const selected = accepted.find((option) => option.value === type);

  return (
    <section className="rounded-2xl border border-border bg-card p-5">
      <h2 className="text-base font-extrabold tracking-tight text-ink">Documents</h2>
      <p className="mt-1 text-xs text-ink-muted">
        One photo ID is required. Anything else is optional but helps us decide faster.
      </p>

      {application.documents.length > 0 && (
        <ul className="mt-4 divide-y divide-border border-y border-border">
          {application.documents.map((document) => (
            <li key={document.id} className="flex items-center justify-between gap-3 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <FileText className="h-4 w-4 shrink-0 text-ink-muted" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-ink">{document.type_label}</p>
                  <p className="truncate text-[11px] text-ink-subtle">
                    {document.original_name}
                    {document.review_note ? ` · ${document.review_note}` : ""}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                    document.status === "approved"
                      ? "bg-primary/15 text-primary"
                      : document.status === "rejected"
                        ? "bg-destructive/15 text-destructive"
                        : "bg-muted text-ink-muted"
                  }`}
                >
                  {document.status}
                </span>
                {application.can_edit && (
                  <button
                    type="button"
                    onClick={() => remove.mutate(document.id)}
                    aria-label={`Remove ${document.type_label}`}
                    className="rounded-lg p-1.5 text-ink-muted transition hover:bg-muted hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {application.can_edit && (
        <div className="mt-4 space-y-3">
          <Field label="Document type">
            <select
              value={type}
              onChange={(event) => setType(event.target.value)}
              className={inputClass}
            >
              {accepted.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                  {option.proves_identity ? " — counts as ID" : ""}
                </option>
              ))}
            </select>
          </Field>

          {selected && <p className="text-[11px] text-ink-subtle">{selected.hint}</p>}

          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.currentTarget.value = "";
              if (file) upload.mutate(file);
            }}
          />

          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={upload.isPending}
            className="inline-flex h-11 items-center gap-2 rounded-xl border border-border bg-background px-5 text-sm font-bold text-ink transition hover:bg-muted disabled:opacity-60"
          >
            {upload.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            {upload.isPending ? "Uploading…" : "Upload document"}
          </button>
        </div>
      )}
    </section>
  );
}

function SubmitSection({
  application,
  onSubmitted,
}: {
  application: HostApplication;
  onSubmitted: () => Promise<void>;
}) {
  const submit = useMutation({
    mutationFn: () => hostApplicationService.submit(),
    onSuccess: async (response) => {
      toast.success(response.message);
      await onSubmitted();
    },
    onError: (error) => toast.error(extractErrorMessage(error, "Couldn't submit your application")),
  });

  if (!application.can_edit) return null;

  return (
    <section className="rounded-2xl border border-border bg-card p-5">
      {/*
        The outstanding list comes from the server, so a disabled button always
        has a reason next to it. Refusing to submit without saying why is the
        thing this avoids.
      */}
      {application.outstanding.length > 0 ? (
        <>
          <h2 className="text-base font-extrabold tracking-tight text-ink">Before you submit</h2>
          <ul className="mt-2 space-y-1.5">
            {application.outstanding.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-ink-muted">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                {item}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="flex items-center gap-2 text-sm font-semibold text-ink">
          <CheckCircle2 className="h-4 w-4 text-primary" />
          Everything we need is here.
        </p>
      )}

      <button
        type="button"
        onClick={() => submit.mutate()}
        disabled={!application.can_submit || submit.isPending}
        className="mt-4 inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-bold text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
      >
        {submit.isPending ? "Submitting…" : "Submit for review"}
        {!submit.isPending && <ArrowRight className="h-4 w-4" />}
      </button>
      <p className="mt-2 text-center text-[11px] text-ink-subtle">
        We review applications within two working days.
      </p>
    </section>
  );
}

const inputClass =
  "h-11 w-full rounded-xl border border-border bg-background px-3 text-sm text-ink outline-none placeholder:text-ink-subtle focus:border-primary/50 focus:ring-2 focus:ring-primary/15 disabled:opacity-60";

function Field({
  label,
  required = false,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-ink-muted">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </span>
      {children}
    </label>
  );
}
